import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { createClerkSupabaseClient } from '@/lib/supabase';
import { useAuth } from '@/lib/hooks';
import { useGetToken } from '@/lib/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Building2, AlertCircle } from 'lucide-react';

const createOrganizationSchema = z.object({
  name: z.string().min(2, 'Organization name must be at least 2 characters'),
  description: z.string().optional(),
  website: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
  industry: z.string().optional()
});

type CreateOrganizationForm = z.infer<typeof createOrganizationSchema>;

interface CreateOrganizationStepProps {
  data: any;
  onUpdate: (data: any) => void;
  onNext: () => void;
}

export function CreateOrganizationStep({ data, onUpdate, onNext }: CreateOrganizationStepProps) {
  const { user } = useAuth();
  const getToken = useGetToken();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create authenticated Supabase client
  const supabase = createClerkSupabaseClient(getToken)

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<CreateOrganizationForm>({
    resolver: zodResolver(createOrganizationSchema),
    defaultValues: {
      name: data.name || '',
      description: data.description || '',
      website: data.website || '',
      industry: data.industry || ''
    }
  });

  const onSubmit = async (formData: CreateOrganizationForm) => {
    setIsLoading(true);
    setError(null);

    try {
      const client = supabase;

      // First, ensure the user exists in the local users table
      const { error: userUpsertError } = await client
        .from('users')
        .upsert({
          id: user!.id,
          email: user!.email || '',
          first_name: user!.user_metadata?.full_name?.split(' ')[0] || '',
          last_name: user!.user_metadata?.full_name?.split(' ').slice(1).join(' ') || '',
          role_id: null // Will be set later if needed
        }, {
          onConflict: 'id'
        });

      if (userUpsertError) throw userUpsertError;

      // Create organization
      const { data: org, error: orgError } = await client
        .from('organizations')
        .insert({
          name: formData.name,
          description: formData.description,
          website: formData.website || null,
          created_by: user!.id,
          metadata: {
            industry: formData.industry
          }
        })
        .select()
        .single();

      if (orgError) throw orgError;

      // Add current user as founder
      const { error: userOrgError } = await client
        .from('organization_members')
        .insert({
          organization_id: org.id,
          user_id: user!.id,
          role: 'owner'
        });

      if (userOrgError) throw userOrgError;

      // Update getting started checklist
      await client
        .from('users')
        .update({
          getting_started_checklist: {
            created_organization: true
          }
        })
        .eq('id', user!.id);

      // Track analytics
      await client
        .from('onboarding_analytics')
        .insert({
          user_id: user!.id,
          event_name: 'organization_created',
          event_data: { organization_id: org.id }
        });

      // Update step data
      onUpdate({
        ...formData,
        organizationId: org.id
      });

      onNext();
    } catch (err: any) {
      console.error('Error creating organization:', err);
      setError(err.message || 'Failed to create organization');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Building2 className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold">Set up your organization</h3>
          <p className="text-sm text-muted-foreground">
            This will be the parent entity for all your equity pools
          </p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h4 className="font-medium text-blue-900 mb-2">Why Organizations & Projects?</h4>
        <div className="text-sm text-blue-800 space-y-2">
          <p>
            <strong>Organizations</strong> represent your company or business entity, while <strong>Projects</strong> (equity pools) represent separate cap tables within your organization.
          </p>
          <p>This structure supports complex scenarios like:</p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li>Multiple funding rounds (Series A, Series B) as separate projects</li>
            <li>Different product lines with their own equity pools</li>
            <li>Subsidiaries or spin-offs managed as separate projects</li>
            <li>Different equity pools for different employee groups</li>
          </ul>
          <p className="text-xs text-blue-600 mt-2">
            Example: Google (organization) with Search, YouTube, Cloud (separate equity pools)
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Organization Name *</Label>
          <Input
            id="name"
            placeholder="Acme Inc."
            {...register('name')}
            disabled={isLoading}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            placeholder="What does your organization do?"
            rows={3}
            {...register('description')}
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="website">Website</Label>
          <Input
            id="website"
            type="url"
            placeholder="https://example.com"
            {...register('website')}
            disabled={isLoading}
          />
          {errors.website && (
            <p className="text-sm text-destructive">{errors.website.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="industry">Industry</Label>
          <Input
            id="industry"
            placeholder="Technology, Healthcare, Finance, etc."
            {...register('industry')}
            disabled={isLoading}
          />
        </div>

        <div className="pt-4">
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Creating Organization...' : 'Create Organization'}
          </Button>
        </div>
      </form>
    </div>
  );
}